import { useMemo, useState } from 'react';
import { Stack, SearchInput, StackItem, Container } from '@optimus-web/core';
import { createZodFormResolver, lt, useFeatureFlag, useFormControls } from '@mews/framework';
import { eqUniversal } from '@mews/fp';
import { FeatureFlag } from '@mews-commander/enums';
import { ownerTypes } from '../../constants';
import {
    useCityLedgerFilterContext,
    useCityLedgerPagination,
    useCityLedgerSelectedDate,
} from '../../context/CityLedgerFilterContext';
import { OwnerTypeFilter, LedgersFiltersPanelWrapper, LedgersDatePicker } from '../../../common/filters';
import { AgeingFilter } from './AgeingFilter';
import { CityLedgerFilterValues, filtersSchema, FiltersValueKey } from './utils/schema';
import { defaultFilterValues } from './utils/defaultFilterValues';

export const Filters = () => {
    const [query, setQuery] = useState('');
    const {
        filterValues: { searchQuery, ...filterPanelValues },
        setFilterValue,
        setFilterValues,
        resetFilterValues,
    } = useCityLedgerFilterContext();
    const enableFilteringHighlights = useFeatureFlag(FeatureFlag.filteringHighlightsInCityLedger);
    const { resetPagination } = useCityLedgerPagination();
    const { searchQuery: defaultSearchQuery, ...defaultValuesWithoutSearchQuery } = defaultFilterValues;
    const { selectedDate, setSelectedDate } = useCityLedgerSelectedDate();

    const filterPanelFiltersAreApplied = useMemo(
        () => !eqUniversal.equals(filterPanelValues, defaultValuesWithoutSearchQuery),
        [filterPanelValues, defaultValuesWithoutSearchQuery]
    );

    const handleSubmit = (values: CityLedgerFilterValues) => {
        const { searchQuery: _search, ...rest } = values;
        setFilterValues(rest);
        resetPagination();
    };

    const handleFilterCloseWithoutChange = () => {
        reset({ searchQuery, ...filterPanelValues });
    };

    const { renderFormContent, submit, errors, reset } = useFormControls<CityLedgerFilterValues>({
        id: 'city-ledger-filter',
        defaultValues: {
            searchQuery: searchQuery || defaultSearchQuery,
            ...defaultValuesWithoutSearchQuery,
            ...filterPanelValues,
        },
        mode: 'onSubmit',
        resolver: createZodFormResolver(filtersSchema),
        onSubmit: handleSubmit,
    });

    const handleReset = () => {
        reset({ searchQuery, ...defaultValuesWithoutSearchQuery });
        resetFilterValues({ searchQuery, ...defaultValuesWithoutSearchQuery });
        resetPagination();
    };

    return renderFormContent(
        <Container>
            <Stack spacing="100">
                <Container minWidth="300">
                    <SearchInput
                        data-test-id="search-field"
                        name={FiltersValueKey.searchQuery}
                        placeholder={lt('CityLedgerSearchByOwnerOrInvoice')}
                        type="text"
                        maxLength={120}
                        clearable
                        value={query ?? ''}
                        onChange={(_event, value) => {
                            setQuery(value.toString() ?? '');
                        }}
                        onSearch={(value: string) => {
                            setFilterValue(FiltersValueKey.searchQuery, value);
                            resetPagination();
                        }}
                        onClear={() => {
                            setFilterValue(FiltersValueKey.searchQuery, '');
                            setQuery('');
                            resetPagination();
                        }}
                    />
                </Container>
                {enableFilteringHighlights && (
                    <LedgersDatePicker selectedDate={selectedDate} setSelectedDate={setSelectedDate} />
                )}
                <StackItem data-test-id="filters-panel">
                    <LedgersFiltersPanelWrapper
                        title={lt('CityLedgerFilters')}
                        filterPanelFiltersAreApplied={filterPanelFiltersAreApplied}
                        submit={submit}
                        resetFilters={handleReset}
                        errors={errors}
                        onClose={handleFilterCloseWithoutChange}
                    >
                        <OwnerTypeFilter
                            name={FiltersValueKey.ownerTypes}
                            ownerTypesOptions={ownerTypes}
                            helper={lt('CityLedgerFilterByGuestOrCompany')}
                        />
                        <AgeingFilter name={FiltersValueKey.ageing} />
                    </LedgersFiltersPanelWrapper>
                </StackItem>
            </Stack>
        </Container>
    );
};
